import { useMutation, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { rolesListQuery } from "./api/queries";
import { FormItemWithMessage } from "@/components/FormItemWithMessage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { graphql } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";
import { transformForApi } from "@/lib/utils/transforms";
import { createRequiredStringSchema, createRequiredEmailSchema } from "@/lib/utils/validation/zod";

const inviteUserSchema = z.object({
  firstName: createRequiredStringSchema("first name", 50),
  lastName: createRequiredStringSchema("last name", 50),
  email: createRequiredEmailSchema("email"),
  roleId: z.string().min(1, "Role is required"),
});

type InviteUserForm = z.infer<typeof inviteUserSchema>;

interface UserInviteFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function UserInviteForm({ open, onOpenChange, onSuccess }: UserInviteFormProps) {
  const { activeBusinessUnitIdInt } = useActiveBusinessUnit();
  const { toast } = useToast();

  // Fetch roles for the dropdown
  const { data: rolesData, loading: rolesLoading } = useQuery(rolesListQuery);
  const roles = (rolesData?.roles || []).filter(Boolean);

  const form = useForm<InviteUserForm>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      roleId: "",
    },
  });

  const [inviteUser, { loading }] = useMutation(inviteUserMutation);

  const onSubmit = async (data: InviteUserForm) => {
    if (!activeBusinessUnitIdInt) {
      toast({
        title: "Error",
        description: "No active business unit selected",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await inviteUser({
        variables: {
          input: {
            firstName: transformForApi.text(data.firstName),
            lastName: transformForApi.text(data.lastName),
            email: transformForApi.email(data.email),
            businessUnit: activeBusinessUnitIdInt,
            role: parseInt(data.roleId),
            requireAccept: true,
          },
        },
      });

      const errors = result.errors || [];
      if (errors.length > 0) {
        toast({
          title: "Failed to send invitation",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.inviteUser?.errors?.length) {
        toast({
          title: "Failed to send invitation",
          description: extractErrorMessages(result.data.inviteUser.errors),
          variant: "destructive",
        });
        return;
      }

      if (result.data?.inviteUser?.businessUnitUser) {
        toast({
          title: "Invitation sent",
          description: `Successfully invited ${form.getValues("firstName")} ${form.getValues("lastName")}`,
        });
        form.reset();
        onOpenChange(false);
        onSuccess?.();
      }
    } catch (error) {
      toast({
        title: "Failed to send invitation",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Send an invitation to a new team member. They'll receive an email to join your business unit.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItemWithMessage
                    label="First Name"
                    required
                    inputComponent={<Input placeholder="Enter first name" {...field} />}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItemWithMessage
                    label="Last Name"
                    required
                    inputComponent={<Input placeholder="Enter last name" {...field} />}
                  />
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItemWithMessage
                  label="Email"
                  required
                  inputComponent={<Input type="email" placeholder="Enter email address" {...field} />}
                />
              )}
            />
            <FormField
              control={form.control}
              name="roleId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value} disabled={rolesLoading}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {roles.map(
                        (role) =>
                          role && (
                            <SelectItem key={role.id} value={role.id}>
                              {role.name}
                            </SelectItem>
                          ),
                      )}
                    </SelectContent>
                  </Select>
                  <div className="min-h-[20px]">
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="gap-2">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

const inviteUserMutation = graphql(`
  mutation InviteUser($input: InviteUserInput!) {
    inviteUser(input: $input) {
      businessUnitUser {
        id
        status
        isOrganisationOwner
        user {
          id
          firstName
          lastName
          email
        }
      }
      errors {
        field
        message
      }
    }
  }
`);
